import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { appInsights } from "../../AppInsights";

const APP_NAME = "LASSI-ui";
const DIAGNOSTIC_SOURCE = "lassi-ui-assessment";
const MAX_PROPERTY_LENGTH = 900;

type DiagnosticValue = string | number | boolean | undefined | null | object;

interface DiagnosticTrace {
    enabled?: boolean;
    area: string;
    event: string;
    properties?: Record<string, DiagnosticValue>;
    measurements?: Record<string, number>;
    severityLevel?: SeverityLevel;
}

const truncate = (value: string): string =>
    value.length > MAX_PROPERTY_LENGTH ? `${value.slice(0, MAX_PROPERTY_LENGTH)}...` : value;

const toAppInsightsValue = (value: DiagnosticValue): string | number | boolean | undefined => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === "string") return truncate(value);
    if (typeof value === "number" || typeof value === "boolean") return value;

    try {
        return truncate(JSON.stringify(value));
    } catch {
        return "[unserializable]";
    }
};

const getDeviceProperties = (): Record<string, string | number | boolean | undefined> => ({
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: (navigator as any).deviceMemory,
    language: navigator.language,
    online: navigator.onLine,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    path: window.location.pathname
});

export const trackDiagnosticTrace = ({
    enabled,
    area,
    event,
    properties = {},
    measurements,
    severityLevel = SeverityLevel.Information
}: DiagnosticTrace): void => {
    if (!enabled) return;

    const sanitizedProperties = Object.entries({
        appName: APP_NAME,
        diagnosticSource: DIAGNOSTIC_SOURCE,
        diagnosticArea: area,
        diagnosticEvent: event,
        ...getDeviceProperties(),
        ...properties
    }).reduce<Record<string, string | number | boolean>>((acc, [key, value]) => {
        const sanitizedValue = toAppInsightsValue(value);

        if (sanitizedValue !== undefined) {
            acc[key] = sanitizedValue;
        }

        return acc;
    }, {});

    appInsights.trackTrace(
        {
            message: `[${APP_NAME}][${area}] ${event}`,
            severityLevel,
            properties: sanitizedProperties
        },
        measurements
    );
};

export const trackDiagnosticException = ({
    enabled,
    area,
    event,
    error,
    properties = {}
}: DiagnosticTrace & { error: unknown }): void => {
    if (!enabled) return;

    trackDiagnosticTrace({
        enabled,
        area,
        event,
        properties: {
            ...properties,
            errorName: error instanceof Error ? error.name : undefined,
            errorMessage: error instanceof Error ? error.message : `${error}`
        },
        severityLevel: SeverityLevel.Error
    });
};
